import type { ObjectLiteral } from 'api/submission';
import {
  fetchVintelligenceDecodeVIN,
  fetchVintelligenceMakes,
  fetchVintelligenceModels,
  REQUESTED_FIELDS_FROM_VINTELLIGENCE,
} from 'api/vintelligence';
import otherVehiclesIcon from 'assets/icons/other-vehicles.svg';
import pickupIcon from 'assets/icons/pickup.svg';
import trailerIcon from 'assets/icons/trailer.svg';
import truckIcon from 'assets/icons/truck.svg';
import vanIcon from 'assets/icons/van.svg';
import { entityTypes, STATES, VIN_LENGTH, VIN_REGEX } from 'common/constants';
import type { IToggleButton } from 'components/lib/ToggleButton';
import { isNil, reduce } from 'lodash-es';
import type { IVehicle, IVehicleGCWTable } from '../../Vehicles.types';
import type {
  IGetDepreciatedCost,
  IGetVehSubtype,
  IGetVehType,
  IShouldShowVehicleStatedAmountHelperParams,
  IVerifyVINHelperParams,
  IVerifyVINHelperResult,
} from './VehicleModal.types';

/**
 * Validates VIN format
 * VIN must be exactly 17 characters and contain only letters and numbers
 * @param vin - The VIN to validate
 * @returns true if valid, false otherwise
 */
export const validateVINFormat = (vin: string): boolean => {
  if (!vin) return false;

  // VIN must be exactly 17 characters
  if (vin.length !== VIN_LENGTH) return false;

  // VIN must contain only letters and numbers (no spaces, hyphens, or special characters)
  return VIN_REGEX.test(vin);
};

export const getVehicleBodyCategoryIconProps = (
  vehicleBodyCategory: string,
): Pick<IToggleButton, 'fillType' & 'endAdornment'> => {
  switch (vehicleBodyCategory) {
    case 'Cars, Pickup, or SUV': {
      return {
        fillType: 'full',
        endAdornment: {
          src: pickupIcon,
          type: 'icon',
        },
      };
    }

    case 'Trucks': {
      return {
        endAdornment: {
          src: truckIcon,
          type: 'icon',
        },
      };
    }

    case 'Vans': {
      return {
        endAdornment: {
          src: vanIcon,
          type: 'icon',
        },
      };
    }

    case 'Trailer': {
      return {
        endAdornment: {
          src: trailerIcon,
          type: 'icon',
        },
      };
    }

    default: {
      return {
        endAdornment: {
          src: otherVehiclesIcon,
          type: 'icon',
        },
      };
    }
  }
};

export const verifyVIN = async ({
  veh_vin,
  bodyStyleToCategoryMapping,
  gcwMapping,
  tblGCW,
  tblDepreciation,
  yearAdded,
  consumer_submission_id,
  // eslint-disable-next-line complexity
}: IVerifyVINHelperParams) => {
  const decodedVIN = await fetchVintelligenceDecodeVIN({
    requested_fields: REQUESTED_FIELDS_FROM_VINTELLIGENCE,
    vin_requests: [
      {
        vin: veh_vin,
      },
    ],
    consumer_submission_id,
  });

  const result: IVerifyVINHelperResult = {
    status: 'fail',
    updatedFields: {
      veh_vin_isvalid: 'No',
    },
  } as IVerifyVINHelperResult;

  if (decodedVIN && decodedVIN.length > 0) {
    const relatedVINElement = decodedVIN.find(
      (decodedVINElement) => decodedVINElement.vin === veh_vin,
    );

    if (relatedVINElement) {
      const returnCodeNumber = Number(relatedVINElement.returnCode);

      const decodedFields =
        reduce(
          relatedVINElement.fields,
          (acc, field) => ({ ...acc, [`${field.name}`]: field.value ?? null }),
          {} as Record<string, string | null>,
        ) ?? {};

      if (!isNil(returnCodeNumber) && returnCodeNumber < 4) {
        const findedCategory = bodyStyleToCategoryMapping?.find(
          (c) => c.body_style_cd === decodedFields?.BODY_STYLE_CD,
        );

        const findedGVW = gcwMapping.find(
          (gcw) =>
            parseInt(decodedFields?.NADA_GVWC1!, 10) >= gcw.gvwMin &&
            parseInt(decodedFields?.NADA_GVWC1!, 10) <= gcw.gvwMax,
        );

        const veh_subtype = getVehicleSubtype({
          veh_body_category: findedCategory?.veh_body_category || '',
          veh_body_subcategory: findedCategory?.veh_body_subcategory || '',
          veh_type: findedCategory?.veh_type || '',
          veh_gvw: findedGVW?.dropdown || '',
          gcwMapping,
          tblGCW,
        });

        const veh_msrp_dpr = getDepreciatedCost({
          veh_msrp: decodedFields?.NADA_MSRP1 || '',
          veh_year: decodedFields?.MDL_YR || '',
          yearAdded,
          tblDepreciation,
        });

        const veh_stated_amt = shouldShowVehicleStatedAmount({
          vehMSRP: decodedFields?.NADA_MSRP1 || '',
          vehBodyCategory: findedCategory?.veh_body_category || '',
          vehYear: decodedFields?.MDL_YR || '',
          yearAdded,
        })
          ? ''
          : decodedFields?.NADA_MSRP1 || '';

        const updatedFields: Partial<IVehicle> = {
          ...result.updatedFields,
          veh_year: decodedFields?.MDL_YR || '',
          veh_make: decodedFields?.MAK_NM || '',
          veh_model: decodedFields?.MDL_DESC || '',
          veh_trim: decodedFields?.TRIM_DESC || '',
          veh_gcwr: decodedFields?.NADA_GCW1 || '',
          veh_gvwr: decodedFields?.NADA_GVWC1 || '',
          veh_msrp: decodedFields?.NADA_MSRP1 || '',
          veh_gvw: findedGVW?.dropdown,
          veh_body_category: findedCategory?.veh_body_category,
          veh_body_subcategory: findedCategory?.veh_body_subcategory,
          veh_subtype,
          veh_msrp_dpr,
          veh_stated_amt,
          veh_type: findedCategory?.veh_type,
          veh_vin_isvalid: 'Yes',
        };

        // Fetch Makes
        const vehicleMakes = await fetchVintelligenceMakes({
          year: updatedFields.veh_year!,
          consumer_submission_id,
        });

        if (vehicleMakes) {
          result.vehMakeChoices = vehicleMakes.map((make) => ({
            code: make.code,
            name: make.description,
          }));

          // Fetch Models
          const currentMake = vehicleMakes.find(
            (make) => make.description === updatedFields.veh_make,
          );
          if (updatedFields.veh_year && updatedFields.veh_make && currentMake) {
            updatedFields.veh_make = currentMake?.description;
            const vehicleModels = await fetchVintelligenceModels({
              year: updatedFields.veh_year,
              make: currentMake?.code,
              consumer_submission_id,
            });

            if (vehicleModels) {
              result.vehModelChoices = vehicleModels.map((model) => ({
                code: model.code,
                name: model.description,
              }));
            }
          }
        }

        result.updatedFields = updatedFields;
        result.status = 'success';
      }
    }
  }

  return result;
};

const roundToNearest100 = (num: number): number => Math.round(num / 100) * 100;

/**
 * Determine the `VEH_MSRP_DPR`
 *
 * https://dev.azure.com/radity-gmbh/THREE-insurance/_workitems/edit/11178/
 */
export const getDepreciatedCost = ({
  veh_msrp,
  veh_year,
  yearAdded,
  tblDepreciation,
}: IGetDepreciatedCost) => {
  const tmpVehMSRP = parseInt(veh_msrp, 10);
  const tmpVehYear = parseInt(veh_year, 10);
  const tmpYearAdded = parseInt(yearAdded, 10);

  const lag = tmpYearAdded - tmpVehYear;

  let dprFactor = 1;

  if (lag <= 0) {
    dprFactor = 1.0;
  } else {
    const lagLookup = Math.min(lag, 15);

    dprFactor = tblDepreciation?.find((dep) => dep.lag === lagLookup)?.dprFactor!;
  }

  return String(roundToNearest100(tmpVehMSRP * dprFactor * 1.05));
};

/**
 * Determine the `VEH_TYPE` with `VEH_BODY_CATEGORY` & `VEH_BODY_SUBCATEGORY`
 *
 * https://dev.azure.com/radity-gmbh/THREE-insurance/_workitems/edit/11485/
 */
export const getVehicleType = ({
  policyData,
  bodyCategory,
  bodySubCategory,
  table,
  vehicleLocationState,
  locFilingSetId,
  // eslint-disable-next-line complexity
}: IGetVehType): string => {
  const vehicleType =
    table?.find(
      (c) =>
        c.veh_body_category === bodyCategory &&
        c.veh_body_subcategory === bodySubCategory &&
        c.state === vehicleLocationState &&
        c.FilingSetID === Number(locFilingSetId),
    )?.veh_type ?? '';

  if (vehicleType === 'Special') {
    return 'TTT';
  }

  // Get final vehicle type value according to the business rules and location
  // Please inform the backend when the state changes, this step is often forgotten
  switch (vehicleLocationState) {
    case STATES.AZ:
    case STATES.CO:
    case STATES.CT:
    case STATES.IL:
    case STATES.IN:
    case STATES.KS:
    case STATES.MD:
    case STATES.MO:
    case STATES.NC:
    case STATES.NJ:
    case STATES.OR:
    case STATES.PA:
    case STATES.SC:
    case STATES.TX:
    case STATES.IA:
    case STATES.CA:
    case STATES.OK:
    case STATES.TN:
    case STATES.UT:
    case STATES.NE:
    case STATES.LA:
    case STATES.WI:
    case STATES.MN:
    case STATES.NV:
    case STATES.GA:
    case STATES.OH:
    case STATES.NM:
    case STATES.NY:
    case STATES.MI:
      // this is special business rule for TX, NC, GA and v15 states
      if (vehicleType === 'SVM') {
        return policyData?.pol_tax_ca_smallvehiclemapping;
      }
      return vehicleType;

    default:
      return vehicleType;
  }
};

/**
 * Determine the `VEH_SUBTYPE`
 *
 * https://dev.azure.com/radity-gmbh/THREE-insurance/_workitems/edit/11486/
 */
export const getVehicleSubtype = ({
  veh_body_category,
  veh_body_subcategory,
  veh_type,
  veh_gvw,
  gcwMapping,
  tblGCW,
  // eslint-disable-next-line complexity
}: IGetVehSubtype): string => {
  const gvwTableEntry = gcwMapping?.find((gcw) => gcw.dropdown === veh_gvw) as
    | IVehicleGCWTable
    | undefined;
  const gvwMin = gvwTableEntry?.gvwMin;
  const gvwMax = gvwTableEntry?.gvwMax;

  const veh_gcw =
    gvwMin !== undefined && gvwMax !== undefined
      ? (tblGCW?.find((gcw) => gcw?.gcwMin! <= gvwMin && gcw?.gcwMax! >= gvwMax)?.dropdown ?? '')
      : '';

  let veh_subtype = '';

  if (veh_body_subcategory === 'Service or Utility Trailer (0 - 2000 lbs load capacity)') {
    veh_subtype = 'Service or Utility Trailer';
  } else if (veh_body_category === 'Trailer') {
    veh_subtype = 'Semitrailer';
  } else if (veh_type === 'TTT' && veh_body_subcategory === 'Truck Tractor') {
    // lookup mapping and find a match
    const foundInMapping = tblGCW?.find((mapping) => veh_gcw === mapping.dropdown);

    if (foundInMapping) {
      veh_subtype = foundInMapping?.veh_subtype ?? '';
    }
  } else if (veh_type === 'TTT' && veh_body_subcategory !== 'Truck Tractor') {
    // lookup mapping and find a match
    const foundInMapping = gcwMapping?.find((mapping) => veh_gvw === mapping.dropdown);

    if (foundInMapping) {
      veh_subtype = foundInMapping?.veh_subtype ?? '';
    }
  }

  return veh_subtype;
};

/**
 *
 * Determine whether to show `VEH_STATED_AMT`
 *
 * https://linear.app/three-insurance/issue/ENG-7103/fe-calculate-and-display-vehicle-depreciated-cost-and-cost-new-on
 */
export const shouldShowVehicleStatedAmount = ({
  vehMSRP,
  vehBodyCategory,
  vehYear,
  yearAdded,
}: IShouldShowVehicleStatedAmountHelperParams) => {
  const SPECIAL_VEHICLE_CATEGORIES = [
    'Trailer',
    'Stretch Limousine',
    'Food Truck',
    'Ice Cream Truck',
  ];

  // Show if VINtelligence does not return a value or returns 0 for NADA_MSRP1 for the specific trim
  if (!Number(vehMSRP)) {
    return true;
  }

  // Show for special vehicle categories
  if (SPECIAL_VEHICLE_CATEGORIES.includes(vehBodyCategory)) {
    return true;
  }

  // Show for vehicles older than 15 years
  const vehicleAge = Number(yearAdded) - Number(vehYear);
  return vehicleAge > 15;
};

/**
 *
 * Determine whether the vehicle is prohibited to add
 *
 * https://linear.app/three-insurance/issue/ENG-7367/add-frontend-validation-on-consumer-portal-to-prevent-saving-private
 */
export const checkIsVehicleProhibitedToAdd = ({
  policy_data,
  veh_type,
}: {
  policy_data?: ObjectLiteral;
  veh_type: string;
}) => {
  const { pol_state, pol_legal_entity, pol_tax_ca_industry } = policy_data || {};

  if (
    [STATES.NJ, STATES.NC, STATES.MI, STATES.NY].includes(pol_state) &&
    pol_legal_entity === entityTypes.SOLE_PROPRIETOR.code
  ) {
    if (
      (!['Publics', 'For-Hire Trucking'].includes(pol_tax_ca_industry) &&
        ['PPT', 'TTT', 'Public', 'Special'].includes(veh_type)) ||
      (['Publics', 'For-Hire Trucking'].includes(pol_tax_ca_industry) && veh_type === 'PPT')
    ) {
      return true;
    }
  }

  return false;
};
